import { useMemo } from "react";
import styled from "styled-components";
import { Conditional, Spinner } from "@sablier/v2-components/atoms";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Empty } from "~/client/components/atoms";
import { Characteristic } from "~/client/components/molecules";
import {
  useAccount,
  useChain,
  useExpected,
  usePollingAwareness,
} from "~/client/hooks";
import Footer from "./Footer";
import Item from "./Item";
import Note from "./Note";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    width: 100%;
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    position: relative;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    align-content: center;
    width: 100%;
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
    padding-top: calc(${(props) => props.theme.sizes.edge} * 1);
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme.colors.border};
`;

const Box = styled.div`
  --size: 18px;
  width: 100%;
  padding: calc(${(props) => props.theme.sizes.edge} * 1);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.dark050};
`;

const Loading = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    justify-content: center;
    width: 100%;
    min-height: 100px;
    padding: calc(${(props) => props.theme.sizes.edge} * 1);
  }
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    width: 100%;
    overflow: auto;
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${Content} {
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Content} {
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      padding-bottom: 0;
    }
    ${Box} {
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    }
  }
`;

function Body() {
  usePollingAwareness({ condition: true, page: "dashboard" });
  const { t } = useT();

  const { configured, list: all } = useExpected();

  const { address, isConnected } = useAccount();
  const { chain } = useChain();
  const isLoading = useMemo(() => !isConnected, [isConnected]);
  const isEmpty = useMemo(() => configured.length === 0, [configured]);

  const placeholder = useMemo(
    () =>
      `${t("modalCards.expected.empty")}${
        all.length !== 0 ? t("modalCards.expected.switch") : ""
      }`,
    [all, t],
  );

  return (
    <Wrapper>
      <Content>
        <Note />
        <Divider />
        <Conditional on={isLoading}>
          <Loading>
            <Spinner />
          </Loading>
        </Conditional>
        <Conditional on={!isLoading}>
          <Box>
            <Characteristic.Expected chain={chain} owner={address} />
          </Box>
        </Conditional>
        <Conditional on={!isLoading && isEmpty}>
          <Empty content={placeholder} />
        </Conditional>
        <Conditional on={!isLoading && !isEmpty}>
          <List>
            {configured.map((item) => (
              <Item key={item.id} {...item} />
            ))}
          </List>
        </Conditional>
      </Content>
      <Footer />
    </Wrapper>
  );
}

export default Body;
